import { useEffect, useRef } from 'react';

import lottie from 'lottie-web';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';

import { LOTTIE_TOOLS_LOADING_PATH } from '#client/components/utils/constants';

// Animation segments for VeloToolsLoading
// intro: plays once at start, loop: plays continuously after intro
const ANIMATION_SEGMENTS = {
  intro: [0, 7000],
  loop: [7000, 7200]
};

/**
 * VeloToolsLoading Component
 *
 * A component that displays a Lottie loading animation for tools/generation states.
 * Plays an intro segment first, then continuously loops the loop segment.
 *
 * @param {object} props - Component props
 * @param {number} props.width - The width of the animation (default: 148)
 * @param {number} props.height - The height of the animation (default: 148)
 * @returns {import('react').ReactElement} The VeloToolsLoading component
 */
export default function VeloToolsLoading({ width = 600, height = 600 }) {
  const containerRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    if (!containerRef.current) return () => {};

    const params = {
      container: containerRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: LOTTIE_TOOLS_LOADING_PATH
    };

    animRef.current = lottie.loadAnimation(params);

    animRef.current.addEventListener('DOMLoaded', () => {
      // Play intro first, then loop segment will follow
      animRef.current.playSegments([ANIMATION_SEGMENTS.intro, ANIMATION_SEGMENTS.loop], true);
    });

    // Handle continuous looping after initial playthrough
    const handleComplete = () => {
      if (animRef.current) {
        animRef.current.playSegments(ANIMATION_SEGMENTS.loop, true);
      }
    };

    animRef.current.addEventListener('complete', handleComplete);
    animRef.current.completeHandler = handleComplete;

    animRef.current.addEventListener('data_failed', (error) => {
      console.error('❌ VeloToolsLoading data failed:', error);
    });

    return () => {
      if (animRef.current) {
        if (animRef.current.completeHandler) {
          animRef.current.removeEventListener('complete', animRef.current.completeHandler);
        }
        animRef.current.destroy();
      }
    };
  }, [width, height]);

  return <Box ref={containerRef} style={{ width, height }} />;
}

VeloToolsLoading.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number
};
